import { Box, Grid, Skeleton, Stack } from '@mui/material';
import { CellRendererSelectorFunc, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { VehicleChangeHistory } from '../../types';
import AGGridHoc from '../../hoc/AGGridHoc';
import { CellTypes, getAllChangeHistory, getChangeHistoryFilterValues } from '../../services/vehicles';
import AppliedFilters from '../VehiclesGrid/AppliedFilters';
import { metaData } from './columnDefinition';
import groupingFn from '../../utils/grouping';

interface ChangeHistoryType {
  areFiltersApplied: any;
  filterModel: any;
  gridRef: any;
  setGridApi: any;
  gridHeight: any;
  gridOptions: any;
  onFilterChanged: any;
  perPage: any;
  quickFilterParser?: any;
  quickFilterMatcher?: any;
  inputText?: any;
  setFilterData?: any;
  getFilterData?: any;
}

const Loader = ({ qty = 7 }) => {
  return (
    <Stack m={3} gap={1}>
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Skeleton animation="wave" height={20} />
      <Stack direction="row" gap={2}>
        <Box flexGrow={1} />
        <Skeleton animation="wave" width={200} height={40} />
        <Skeleton animation="wave" width={100} height={40} />
        <Skeleton animation="wave" width={100} height={40} />
      </Stack>
    </Stack>
  );
};

const ChangeHistory: React.FC<ChangeHistoryType> = (props) => {
  const {
    areFiltersApplied,
    filterModel,
    gridRef,
    gridHeight,
    gridOptions,
    onFilterChanged,
    perPage,
    quickFilterParser,
    quickFilterMatcher,
    inputText,
  } = props;

  const [gridApi, setGridApi] = useState<any>(null);
  const appliedFilter = useSelector((state: RootState) => state.filter.appliedFilter);
  const [groupBy, setGroupBy] = useState<any>();

  let tempData: any = [];
  const getFilterValuesAsync = async (params: any) => {
    const col = params.colDef.field;
    const data = await getChangeHistoryFilterValues(col);
    if (col === 'bulk') {
      data.data = data?.data?.map((a: any) => (a ? 'Bulk' : 'Single'));
    }
    return params.success(data.data);
  };

  const filterParamsTemp = {
    values: getFilterValuesAsync,
  };

  const batchNoFilterParamsTemp = {
    filterOptions: ['inRange'],
    maxNumConditions: 1,
  };

  const columnDefs = useMemo(
    () =>
      metaData?.columnDefs?.map(
        ({ type, headerName, field, sortable, width, filter, hide, valueGetter, valueFormatter }: any) => {
          const cellRenderer = (CellTypes as any)[type];
          const cellRendererSelector: CellRendererSelectorFunc<VehicleChangeHistory> = (params) => {
            return {
              component: cellRenderer,
            };
          };

          return {
            colId: field,
            headerName,
            field,
            sortable,
            width,
            filter,
            tooltipField: field,
            cellRendererSelector,
            cellRenderer,
            hide,
            valueGetter,
            valueFormatter,
            filterParams: field === 'batchNumber' ? batchNoFilterParamsTemp : filterParamsTemp,
            enableRowGroup: true,
          };
        }
      ),
    []
  );

  let tempGroupedData: any = [];

  const datasource = {
    getRows(params: any) {
      console.log(JSON.stringify(params.request, null, 1));
      const { startRow, endRow, rowGroupCols, sortModel, groupKeys, filterModel: paramFilter } = params.request;
      /* Pagination */
      const page = endRow && endRow / 50;

      /* Sorting */
      const sort = `${sortModel[0]?.sort}:${sortModel[0]?.colId}`;
      const sortStatus = sortModel?.length > 0;

      /* Sidebar Filter */
      let tempFilter = {
        temp: { values: [], filterType: 'set', filter: '', operator: '', conditions: [], filterTo: '' },
      };
      const tempFilterKeys: any = [];
      const filterOpCheck = [];
      if (paramFilter) {
        tempFilter = paramFilter;
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(tempFilter)) {
          if (key === 'batchNumber') {
            const repl = (s: any) => {
              return s.toString().replace(/\d+/g, function (m: any) {
                return '000000000'.substring(m.length - 1) + m;
              });
            };

            const temp = `batchNumber:${repl(value?.filter)},${repl(value?.filterTo)}`;
            filterOpCheck.push('batchNumber:RANGE');
            tempFilterKeys.push(temp);
          } else {
            if (value && value?.filter) {
              const temp = `${key}:${value?.filter}`;
              tempFilterKeys.push(temp);
            }
            if (value && value?.operator) {
              const conditions = value?.conditions?.map((a: any) => a?.filter);
              const temp = `${key}:${conditions}`;
              filterOpCheck.push(`${key}:${value?.operator}`);
              tempFilterKeys.push(temp);
            }
            if (value && value?.values?.length > 0) {
              let valueTemp: any = [];
              valueTemp = value?.values;
              if (valueTemp.includes('Bulk')) {
                valueTemp = [true];
              } else if (valueTemp.includes('Single')) {
                valueTemp = [false];
              }
              if (key.includes('createdAt')) {
                valueTemp = valueTemp.map((x: any) => {
                  const d = new Date(x);
                  /* eslint-disable prefer-template */
                  return [d.getFullYear(), ('0' + (d.getMonth() + 1)).slice(-2), ('0' + d.getDate()).slice(-2)].join(
                    '-'
                  );
                });
              }
              const temp = `${key}:${valueTemp}`;
              tempFilterKeys.push(temp);
            }
          }
        }
      }

      /* Grouping */
      const grouping = rowGroupCols?.slice(-1)[0]?.field;
      let groupByData;
      if (groupKeys.length > 0) {
        const myArr = groupKeys[0]?.split('(')[0];
        groupByData = `${grouping}:${myArr}`;
      }
      let arr: any = [];
      if (grouping && arr.length > 0 && groupKeys.length === 0) {
        const numberOfItemsPerPage = 50;
        const start = (page - 1) * numberOfItemsPerPage + 1;
        const end = start + numberOfItemsPerPage;

        const tempNewAllRowData: any = tempGroupedData.slice(start, end);

        let arrTemp: any = [];

        if (grouping && groupKeys.length === 0) {
          arrTemp = groupingFn(grouping, tempNewAllRowData);
        }
        params.successCallback(arrTemp, tempGroupedData.length);
      } else {
        getAllChangeHistory(50, page, sortStatus, sort, inputText, grouping, groupKeys, tempFilterKeys, filterOpCheck)
          .then((newAllRowData: any) => {
            const lastRow = newAllRowData?.totalData ? newAllRowData?.totalData : newAllRowData?.data?.length;
            arr = newAllRowData?.data;
            if (grouping && page === 1) {
              tempGroupedData = newAllRowData?.data;
            }
            if (grouping && groupKeys.length === 0) {
              arr = groupingFn(grouping, newAllRowData?.data);
            }
            tempData = arr;
            params.successCallback(arr, lastRow);
          })
          .catch(() => {
            params.failCallback();
          });
      }
    },
  };

  const onGridReady: any = (params: any) => {
    setGridApi(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridApi) {
      onGridReady(gridApi);
    }
  }, [inputText]);

  const getRowId = useCallback(function (params: GetRowIdParams) {
    return params.data._id;
  }, []);

  useEffect(() => {
    if (groupBy) {
      gridApi?.columnApi?.setRowGroupColumns([groupBy]);
    }
  }, [groupBy]);

  const onColumnRowGroupChanged = (event: any) => {
    if (event.columns && event.columns.slice(-1)[0]?.rowGroupActive) {
      setGroupBy(event.columns.slice(-1)[0]?.colId);
    } else if (event.columns.slice(-1)[0]?.rowGroupActive === false) {
      setGroupBy('');
    }
  };

  if (!columnDefs?.length) return <Loader />;
  return (
    <>
      {areFiltersApplied && <AppliedFilters filterModel={filterModel} gridRef={gridRef} columnDefs={columnDefs} />}
      <Box width="100%" height={`calc(100vh - ${gridHeight})`} className="ag-theme-material">
        <AGGridHoc gridRef={gridRef}>
          <AgGridReact
            {...gridOptions}
            onGridReady={onGridReady}
            columnDefs={columnDefs}
            ref={gridRef}
            onFilterChanged={onFilterChanged}
            // quickFilterParser={quickFilterParser}
            // quickFilterMatcher={quickFilterMatcher}
            cacheBlockSize={perPage}
            paginationPageSize={perPage}
            paginateChildRows
            rowModelType="serverSide"
            maxBlocksInCache={1}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
            // onPaginationChanged={(event) => console.log(event)}
          />
        </AGGridHoc>
      </Box>
    </>
  );
};

export default ChangeHistory;
